import { logger } from "@/core/logging";
import { localStorageService, sessionStorageService } from "@/core/storage";
import type { StorageService } from "@/core/storage/StorageService/StorageService";
import type { PixelMatchPersistence } from "@/core/media/usePixelMatch";
import type { AgelessAssessmentFlowType } from "@orval/model";

export class AssessmentSessionStorageService {
  constructor(private readonly storage: StorageService) {}

  getAuthToken(scope: AssessmentSessionStorageScope): string | null {
    return this.storage.getItemSync<string>(this.getAuthTokenKey(scope));
  }

  setAuthToken(scope: AssessmentSessionStorageScope, token: string): void {
    this.storage.setItemSync<string>(this.getAuthTokenKey(scope), token);
  }

  removeAuthToken(scope: AssessmentSessionStorageScope): void {
    this.storage.removeItemSync(this.getAuthTokenKey(scope));
  }

  /**
   * The funnel locale the backend last captured as the assessment's original language —
   * written at define time and after each mid-session locale sync. Absent for sessions this
   * browser did not define (URL-token links), which must never have their language overwritten
   * by the landing page's locale.
   */
  getSyncedOriginalLanguageLocale(scope: AssessmentSessionStorageScope): string | null {
    return this.storage.getItemSync<string>(this.getSyncedOriginalLanguageLocaleKey(scope));
  }

  setSyncedOriginalLanguageLocale(scope: AssessmentSessionStorageScope, locale: string): void {
    this.storage.setItemSync<string>(this.getSyncedOriginalLanguageLocaleKey(scope), locale);
  }

  removeSyncedOriginalLanguageLocale(scope: AssessmentSessionStorageScope): void {
    this.storage.removeItemSync(this.getSyncedOriginalLanguageLocaleKey(scope));
  }

  getNextStepsPath<TNextStepsPath>(scope: AssessmentSessionStorageScope): TNextStepsPath | null {
    return this.storage.getItemSync<TNextStepsPath>(this.getNextStepsPathKey(scope));
  }

  setNextStepsPath<TNextStepsPath>(
    scope: AssessmentSessionStorageScope,
    path: TNextStepsPath,
  ): void {
    this.storage.setItemSync<TNextStepsPath>(this.getNextStepsPathKey(scope), path);
  }

  removeNextStepsPath(scope: AssessmentSessionStorageScope): void {
    this.storage.removeItemSync(this.getNextStepsPathKey(scope));
  }

  isDemoModeEnabled(scope: AssessmentSessionStorageScope): boolean {
    return this.storage.getItemSync<boolean>(this.getDemoModeKey(scope)) === true;
  }

  setDemoModeEnabled(scope: AssessmentSessionStorageScope): void {
    this.storage.setItemSync<boolean>(this.getDemoModeKey(scope), true);
  }

  isPhotoUploadModeEnabled(scope: AssessmentSessionStorageScope): boolean {
    return this.isDemoModeEnabled(scope);
  }

  setPhotoUploadModeEnabled(scope: AssessmentSessionStorageScope): void {
    this.setDemoModeEnabled(scope);
  }

  /**
   * Legacy: persists/reads the pre-context `at_location=true` flag. Retained
   * so sessions started on the old build keep resolving to In Clinic. New
   * links persist via {@link setContextMode}.
   */
  isAtLocationModeEnabled(scope: AssessmentSessionStorageScope): boolean {
    return this.storage.getItemSync<boolean>(this.getAtLocationModeKey(scope)) === true;
  }

  setAtLocationModeEnabled(scope: AssessmentSessionStorageScope): void {
    this.storage.setItemSync<boolean>(this.getAtLocationModeKey(scope), true);
  }

  getContextMode(scope: AssessmentSessionStorageScope): string | null {
    return this.storage.getItemSync<string>(this.getContextModeKey(scope));
  }

  setContextMode(scope: AssessmentSessionStorageScope, mode: string): void {
    this.storage.setItemSync<string>(this.getContextModeKey(scope), mode);
  }

  getContextLocationId(scope: AssessmentSessionStorageScope): string | null {
    return this.storage.getItemSync<string>(this.getContextLocationKey(scope));
  }

  setContextLocationId(scope: AssessmentSessionStorageScope, locationId: string): void {
    this.storage.setItemSync<string>(this.getContextLocationKey(scope), locationId);
  }

  getStudioCapture(assessmentId: string): PersistedStudioCapture | null {
    return this.storage.getItemSync<PersistedStudioCapture>(this.getStudioCaptureKey(assessmentId));
  }

  getPersistedStudioCapture(assessmentId: string): PersistedStudioCapture | null {
    return this.getStudioCapture(assessmentId);
  }

  setStudioCapture(assessmentId: string, nextValue: PersistedStudioCapture): void {
    this.storage.setItemSync<PersistedStudioCapture>(
      this.getStudioCaptureKey(assessmentId),
      nextValue,
    );
  }

  persistStudioCaptureSelection(
    assessmentId: string,
    selection: PersistedStudioCaptureSelection,
  ): void {
    this.setPersistedStudioCapture(assessmentId, {
      ...this.getPersistedStudioCapture(assessmentId),
      frontResult: selection.frontResult
        ? {
            uploadedImageId: selection.frontResult.uploadedImageId,
          }
        : undefined,
      profileResult: selection.profileResult
        ? {
            profileImageId: selection.profileResult.profileImageId,
            profileSide: selection.profileResult.profileSide,
          }
        : undefined,
      requestedRetake: null,
    });
  }

  persistStudioRetakeRequest(
    assessmentId: string,
    requestedRetake: AssessmentStudioRetakeTarget,
  ): void {
    this.setPersistedStudioCapture(assessmentId, {
      ...this.getPersistedStudioCapture(assessmentId),
      requestedRetake,
    });
  }

  clearPersistedStudioRetakeRequest(assessmentId: string): void {
    const current = this.getPersistedStudioCapture(assessmentId);
    if (!current || !current.requestedRetake) {
      return;
    }

    this.setPersistedStudioCapture(assessmentId, {
      ...current,
      requestedRetake: null,
    });
  }

  getRevealFirstName(assessmentId: string): string | null {
    const persisted = this.storage.getItemSync<PersistedAssessmentRevealContact>(
      this.getRevealContactKey(assessmentId),
    );

    if (typeof persisted?.firstName !== "string") {
      return null;
    }

    const trimmed = persisted.firstName.trim();
    return trimmed || null;
  }

  setRevealFirstName(assessmentId: string, firstName: string): void {
    this.storage.setItemSync<PersistedAssessmentRevealContact>(
      this.getRevealContactKey(assessmentId),
      {
        firstName,
      },
    );
  }

  getPersistedAssessmentRevealFirstName(assessmentId: string): string | null {
    return this.getRevealFirstName(assessmentId);
  }

  persistAssessmentRevealFirstName(assessmentId: string, firstName: string): void {
    const trimmed = firstName.trim();
    if (!trimmed) {
      return;
    }

    try {
      this.setRevealFirstName(assessmentId, trimmed);
    } catch (error) {
      logger.warn("assessment_reveal_contact_session_storage_write_failed", {
        assessmentId,
      });
      logger.error(
        "assessment_reveal_contact_session_storage_write_failed_error",
        undefined,
        error as Error,
      );
    }
  }

  getWellnessPreScanDraft<TDraft>(assessmentId: string): TDraft | null {
    return this.storage.getItemSync<TDraft>(this.getWellnessPreScanDraftKey(assessmentId));
  }

  setWellnessPreScanDraft<TDraft>(assessmentId: string, draft: TDraft): void {
    this.storage.setItemSync<TDraft>(this.getWellnessPreScanDraftKey(assessmentId), draft);
  }

  removeWellnessPreScanDraft(assessmentId: string): void {
    this.storage.removeItemSync(this.getWellnessPreScanDraftKey(assessmentId));
  }

  getLashBrowPreScanDraft<TDraft>(assessmentId: string): TDraft | null {
    return this.storage.getItemSync<TDraft>(this.getLashBrowPreScanDraftKey(assessmentId));
  }

  setLashBrowPreScanDraft<TDraft>(assessmentId: string, draft: TDraft): void {
    this.storage.setItemSync<TDraft>(this.getLashBrowPreScanDraftKey(assessmentId), draft);
  }

  removeLashBrowPreScanDraft(assessmentId: string): void {
    this.storage.removeItemSync(this.getLashBrowPreScanDraftKey(assessmentId));
  }

  hasSeenTransformationHint(
    hintType: AssessmentTransformationHintType,
    assessmentId: string,
  ): boolean {
    return (
      this.storage.getItemSync<boolean>(this.getTransformationHintKey(hintType, assessmentId)) ===
      true
    );
  }

  markTransformationHintSeen(
    hintType: AssessmentTransformationHintType,
    assessmentId: string,
  ): void {
    this.storage.setItemSync(this.getTransformationHintKey(hintType, assessmentId), true);
  }

  getTransformationEditsRemaining(assessmentId: string): number | null {
    return this.storage.getItemSync<number>(this.getTransformationEditsRemainingKey(assessmentId));
  }

  setTransformationEditsRemaining(assessmentId: string, editsRemaining: number): void {
    this.storage.setItemSync(this.getTransformationEditsRemainingKey(assessmentId), editsRemaining);
  }

  removeTransformationEditsRemaining(assessmentId: string): void {
    this.storage.removeItemSync(this.getTransformationEditsRemainingKey(assessmentId));
  }

  getPixelDiffBase64(key: string): string | null {
    return this.storage.getItemSync<string>(this.getPixelDiffValueKey(key));
  }

  setPixelDiffBase64(key: string, base64: string): void {
    this.storage.setItemSync(this.getPixelDiffValueKey(key), base64);
  }

  removePixelDiffBase64(key: string): void {
    this.storage.removeItemSync(this.getPixelDiffValueKey(key));
  }

  getActivePixelDiffKey(scope: string): string | null {
    return this.storage.getItemSync<string>(this.getPixelDiffScopeSlotKey(scope));
  }

  setActivePixelDiffKey(scope: string, key: string): void {
    this.storage.setItemSync(this.getPixelDiffScopeSlotKey(scope), key);
  }

  removeActivePixelDiffKey(scope: string): void {
    this.storage.removeItemSync(this.getPixelDiffScopeSlotKey(scope));
  }

  hasConsentCaptured(assessmentId: string, flowType: AgelessAssessmentFlowType): boolean {
    const key = this.getConsentCapturedKey(assessmentId, flowType);
    if (this.storage.getItemSync<boolean>(key) === true) {
      return true;
    }

    const legacyKey = this.getLegacyConsentCacheKey(assessmentId, flowType);
    const legacy = localStorageService.getItemSync<boolean | string>(legacyKey);
    if (legacy === true || legacy === "true") {
      this.storage.setItemSync<boolean>(key, true);
      try {
        localStorageService.removeItemSync(legacyKey);
      } catch {
        // Best-effort cleanup; safe to retry on next call.
      }
      return true;
    }

    return false;
  }

  markConsentCaptured(assessmentId: string, flowType: AgelessAssessmentFlowType): void {
    this.storage.setItemSync<boolean>(this.getConsentCapturedKey(assessmentId, flowType), true);
  }

  wasExperimentRecorded(assessmentId: string, experimentKey: string): boolean {
    return (
      this.storage.getItemSync<boolean>(
        this.getExperimentRecordedKey(assessmentId, experimentKey),
      ) === true
    );
  }

  markExperimentRecorded(assessmentId: string, experimentKey: string): void {
    this.storage.setItemSync<boolean>(
      this.getExperimentRecordedKey(assessmentId, experimentKey),
      true,
    );
  }

  clearExperimentRecorded(assessmentId: string, experimentKey: string): void {
    this.storage.removeItemSync(this.getExperimentRecordedKey(assessmentId, experimentKey));
  }

  hasViewedFutureSelfTimeline(assessmentId: string): boolean {
    const key = this.getFutureSelfTimelineViewedKey(assessmentId);
    if (this.storage.getItemSync<boolean>(key) === true) {
      return true;
    }

    const legacyKey = this.getLegacyFutureSelfTimelineViewedKey(assessmentId);
    const legacy = localStorageService.getItemSync<string>(legacyKey);
    if (legacy === "1") {
      this.storage.setItemSync<boolean>(key, true);
      try {
        localStorageService.removeItemSync(legacyKey);
      } catch {
        // Best-effort cleanup; safe to retry on next call.
      }
      return true;
    }

    return false;
  }

  markFutureSelfTimelineViewed(assessmentId: string): void {
    this.storage.setItemSync<boolean>(this.getFutureSelfTimelineViewedKey(assessmentId), true);
  }

  getPixelMatchPersistence(): PixelMatchPersistence {
    return {
      readValue: (key) => this.getPixelDiffBase64(key),
      writeValue: (key, base64) => this.setPixelDiffBase64(key, base64),
      removeValue: (key) => this.removePixelDiffBase64(key),
      readScopedActiveKey: (scope) => this.getActivePixelDiffKey(scope),
      writeScopedActiveKey: (scope, key) => this.setActivePixelDiffKey(scope, key),
      removeScopedActiveKey: (scope) => this.removeActivePixelDiffKey(scope),
    };
  }

  clearAssessmentData(): void {
    this.storage.removeItemsByPrefixSync([`${ASSESSMENT_STORAGE_KEY_PREFIX}:`]);
  }

  private setPersistedStudioCapture(assessmentId: string, nextValue: PersistedStudioCapture): void {
    try {
      this.setStudioCapture(assessmentId, nextValue);
    } catch (error) {
      logger.warn("studio_capture_session_storage_write_failed", {
        assessmentId,
      });
      logger.error("studio_capture_session_storage_write_failed_error", undefined, error as Error);
    }
  }

  private getAuthTokenKey(scope: AssessmentSessionStorageScope): string {
    return `${this.getRouteScopePrefix(scope)}:${AUTH_TOKEN_KEY}`;
  }

  private getNextStepsPathKey(scope: AssessmentSessionStorageScope): string {
    return `${this.getRouteScopePrefix(scope)}:${NEXT_STEPS_PATH_KEY}`;
  }

  private getSyncedOriginalLanguageLocaleKey(scope: AssessmentSessionStorageScope): string {
    return `${this.getRouteScopePrefix(scope)}:${SYNCED_ORIGINAL_LANGUAGE_LOCALE_KEY}`;
  }

  private getRouteScopePrefix(scope: AssessmentSessionStorageScope): string {
    const base = `${ASSESSMENT_STORAGE_KEY_PREFIX}:route:${scope.subdomainSlug}:${scope.flowType}`;
    return scope.variant ? `${base}:${scope.variant}` : base;
  }

  private getAssessmentScopePrefix(assessmentId: string): string {
    return `${ASSESSMENT_STORAGE_KEY_PREFIX}:assessment:${assessmentId}`;
  }

  private getPixelDiffCachePrefix(): string {
    return `${ASSESSMENT_STORAGE_KEY_PREFIX}:cache:${PIXEL_DIFF_CACHE_KEY}`;
  }

  private getDemoModeKey(scope: AssessmentSessionStorageScope): string {
    return `${this.getRouteScopePrefix(scope)}:${DEMO_MODE_KEY}`;
  }

  private getAtLocationModeKey(scope: AssessmentSessionStorageScope): string {
    return `${this.getRouteScopePrefix(scope)}:${AT_LOCATION_MODE_KEY}`;
  }

  private getContextModeKey(scope: AssessmentSessionStorageScope): string {
    return `${this.getRouteScopePrefix(scope)}:${CONTEXT_MODE_KEY}`;
  }

  private getContextLocationKey(scope: AssessmentSessionStorageScope): string {
    return `${this.getRouteScopePrefix(scope)}:${CONTEXT_LOCATION_KEY}`;
  }

  private getStudioCaptureKey(assessmentId: string): string {
    return `${this.getAssessmentScopePrefix(assessmentId)}:${STUDIO_CAPTURE_KEY}`;
  }

  private getRevealContactKey(assessmentId: string): string {
    return `${this.getAssessmentScopePrefix(assessmentId)}:${REVEAL_CONTACT_KEY}`;
  }

  private getWellnessPreScanDraftKey(assessmentId: string): string {
    return `${this.getAssessmentScopePrefix(assessmentId)}:${WELLNESS_PRE_SCAN_DRAFT_KEY}`;
  }

  private getLashBrowPreScanDraftKey(assessmentId: string): string {
    return `${this.getAssessmentScopePrefix(assessmentId)}:${LASH_BROW_PRE_SCAN_DRAFT_KEY}`;
  }

  private getTransformationHintKey(
    hintType: AssessmentTransformationHintType,
    assessmentId: string,
  ): string {
    return `${this.getAssessmentScopePrefix(assessmentId)}:${TRANSFORMATION_HINT_KEY}:${hintType}`;
  }

  private getTransformationEditsRemainingKey(assessmentId: string): string {
    return `${this.getAssessmentScopePrefix(assessmentId)}:${TRANSFORMATION_EDITS_REMAINING_KEY}`;
  }

  private getConsentCapturedKey(assessmentId: string, flowType: AgelessAssessmentFlowType): string {
    return `${this.getAssessmentScopePrefix(assessmentId)}:${CONSENT_CAPTURED_KEY}:${flowType}`;
  }

  private getExperimentRecordedKey(assessmentId: string, experimentKey: string): string {
    return `${this.getAssessmentScopePrefix(assessmentId)}:${EXPERIMENT_RECORDED_KEY}:${experimentKey}`;
  }

  private getFutureSelfTimelineViewedKey(assessmentId: string): string {
    return `${this.getAssessmentScopePrefix(assessmentId)}:${FUTURE_SELF_TIMELINE_VIEWED_KEY}`;
  }

  // TODO(2026-07): drop legacy localStorage fallback + key helpers below once the
  // assessmentId-scoped cache rollover has been live for a full release cycle. The
  // legacy entries are orphaned by then because new tabs mint fresh assessment ids.
  private getLegacyConsentCacheKey(
    assessmentId: string,
    flowType: AgelessAssessmentFlowType,
  ): string {
    return `agelessai:assessment:${assessmentId}:${flowType}-consent-accepted`;
  }

  private getLegacyFutureSelfTimelineViewedKey(assessmentId: string): string {
    return `futureself-timeline-viewed:${assessmentId}`;
  }

  private getPixelDiffValueKey(key: string): string {
    return `${this.getPixelDiffCachePrefix()}:${PIXEL_DIFF_VALUE_KEY}:${key}`;
  }

  private getPixelDiffScopeSlotKey(scope: string): string {
    return `${this.getPixelDiffCachePrefix()}:${PIXEL_DIFF_SCOPE_KEY}:${scope}:${PIXEL_DIFF_SLOT_KEY}`;
  }
}

export type AssessmentStudioRetakeTarget = "front" | "profile" | "both";
export type AssessmentTransformationHintType = "swipe" | "view-toggle";
/**
 * Route-scoped state kept for an assessment this browser defined must also be cleared in
 * `AssessmentSessionProvider`'s URL-token branch: a URL token replaces the assessment, not
 * the scope.
 */
export type AssessmentSessionStorageScope = {
  subdomainSlug: string;
  flowType: AgelessAssessmentFlowType;
  /**
   * Optional discriminator for surfaces that must never share a session with the standard
   * funnel at the same (subdomain, flowType) — e.g. the consult-capture page, whose texted
   * token would otherwise clobber (or be reused by) the patient's own funnel session.
   */
  variant?: string;
};

export type PersistedStudioFrontResult = {
  uploadedImageId: string;
};

export type PersistedStudioProfileResult = {
  profileSide: "left" | "right";
  profileImageId: string;
};

export type PersistedStudioCapture = {
  frontResult?: PersistedStudioFrontResult;
  profileResult?: PersistedStudioProfileResult;
  requestedRetake?: AssessmentStudioRetakeTarget | null;
};

export type PersistedStudioCaptureSelection = {
  frontResult?: PersistedStudioCapture["frontResult"] & { photoData?: string };
  profileResult?: PersistedStudioCapture["profileResult"] & { profilePhotoData?: string };
};

type PersistedAssessmentRevealContact = {
  firstName?: string;
};

const ASSESSMENT_STORAGE_KEY_PREFIX = "agelessai:assessment";
const AUTH_TOKEN_KEY = "auth-token";
const NEXT_STEPS_PATH_KEY = "next-steps-path";
const SYNCED_ORIGINAL_LANGUAGE_LOCALE_KEY = "original-language-synced-locale";
// Intentionally keep the legacy persisted storage key for backward compatibility.
// Changing this string would rotate session storage and break existing persisted state.
const DEMO_MODE_KEY = "photo-upload-mode";
const AT_LOCATION_MODE_KEY = "at-location-mode";
const CONTEXT_MODE_KEY = "context-mode";
const CONTEXT_LOCATION_KEY = "context-location-id";
const STUDIO_CAPTURE_KEY = "studio-capture";
const REVEAL_CONTACT_KEY = "reveal-contact";
const WELLNESS_PRE_SCAN_DRAFT_KEY = "wellness-prescan-draft";
const LASH_BROW_PRE_SCAN_DRAFT_KEY = "lashbrow-prescan-draft";
const TRANSFORMATION_HINT_KEY = "transformation-hint";
const TRANSFORMATION_EDITS_REMAINING_KEY = "transformation-edits-remaining";
const CONSENT_CAPTURED_KEY = "consent-captured";
const EXPERIMENT_RECORDED_KEY = "experiment-recorded";
const FUTURE_SELF_TIMELINE_VIEWED_KEY = "future-self-timeline-viewed";
const PIXEL_DIFF_CACHE_KEY = "pixel-diff";
const PIXEL_DIFF_VALUE_KEY = "value";
const PIXEL_DIFF_SCOPE_KEY = "scope";
const PIXEL_DIFF_SLOT_KEY = "slot";

export const assessmentSessionStorageService = new AssessmentSessionStorageService(
  sessionStorageService,
);

export const assessmentPixelMatchPersistence =
  assessmentSessionStorageService.getPixelMatchPersistence();
